import { useQuery } from "@tanstack/react-query";
import {
    getFinancialDetailDaily,
    getFinancialDetailMonthly,
    getFinancialDetailYearly,
    getFinancialTransactionDaily,
    getFinancialTransactionMonthly,
    getFinancialTransactionYearly,
} from "api/booking.api";
import moment from "moment";
import { useState } from "react";
import styled from "styled-components";
import { DatePicker } from "ui/base/datePicker/DatePicker";
import { NoContent } from "ui/base/NoContent/NoContent";
import { scrollbarStyles } from "ui/base/scrollbarStyle/scrollbarStyle";
import { UserIcon } from "ui/icons";
import NoFinancialsFound from "ui/icons/not-found-icons/noTransactions.svg";
import { defaultThemeColors, fonts } from "ui/theme";
import { getAvatarUrl, USDollar } from "utils";

const currentYear = new Date().getFullYear();
const minDate = new Date(currentYear, 0, 1); // January 1st of the current year

const datePickerConfig = {
    daily: {
        format: "MMM DD, YYYY",
        showMonthYearPicker: false,
        showYearPicker: false,
    },
    monthly: {
        format: "MMMM",
        showMonthYearPicker: true,
        showYearPicker: false,
    },
    yearly: {
        format: "YYYY",
        showMonthYearPicker: false,
        showYearPicker: true,
    },
};
export const Financials = () => {
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [activeCard, setActiveCard] = useState("daily");

    // Financials detail
    const { data: dailyFinancials } = useQuery({
        queryKey: ["getFinancialDetailDaily", selectedDate],
        queryFn: () =>
            getFinancialDetailDaily({
                date: moment(selectedDate).format("YYYY-MM-DD"),
            }),
    });

    const { data: monthlyFinancials } = useQuery({
        queryKey: ["getFinancialDetailMonthly", selectedDate.getFullYear()],
        queryFn: () =>
            getFinancialDetailMonthly({ year: selectedDate.getFullYear() }),
    });

    const { data: yearlyFinancials } = useQuery({
        queryKey: ["getFinancialDetailYearly", selectedDate.getFullYear()],
        queryFn: () =>
            getFinancialDetailYearly({ year: selectedDate.getFullYear() }),
    });

    // Financials transaction
    const { data: dailyTransactions } = useQuery({
        queryKey: ["getFinancialTransactionDaily", selectedDate],
        queryFn: () =>
            getFinancialTransactionDaily({
                date: moment(selectedDate).format("YYYY-MM-DD"),
            }),
    });

    const { data: monthlyTransactions } = useQuery({
        queryKey: [
            "getFinancialTransactionMonthly",
            selectedDate.getFullYear(),
            selectedDate.getMonth() + 1,
        ],
        queryFn: () =>
            getFinancialTransactionMonthly({
                year: selectedDate.getFullYear(),
                month: selectedDate.getMonth() + 1,
            }),
    });

    const { data: yearlyTransactions } = useQuery({
        queryKey: ["getFinancialTransactionYearly", selectedDate.getFullYear()],
        queryFn: () =>
            getFinancialTransactionYearly({ year: selectedDate.getFullYear() }),
    });

    const dataObj = {
        daily: dailyTransactions?.data || [],
        monthly: monthlyTransactions?.data || [],
        yearly: yearlyTransactions?.data || [],
    };

    return (
        <Container>
            <IncomeSummary>
                <Card
                    active={activeCard === "daily"}
                    onClick={() => setActiveCard("daily")}
                >
                    <div className="header">
                        <SVGComponent />
                        <h4>Daily Income</h4>
                    </div>
                    <p className="income">
                        {USDollar.format(dailyFinancials?.data?.[0]?.total || 0)}
                    </p>
                </Card>
                <Card
                    active={activeCard === "monthly"}
                    onClick={() => setActiveCard("monthly")}
                >
                    <div className="header">
                        <SVGComponent />
                        <h4>Monthly Income</h4>
                    </div>
                    <p className="income">
                        {USDollar.format(
                            monthlyFinancials?.data?.[0]?.month ===
                                selectedDate.getMonth() + 1
                                ? monthlyFinancials?.data?.[0]?.total || 0
                                : 0
                        )}
                    </p>
                </Card>
                <Card
                    active={activeCard === "yearly"}
                    onClick={() => setActiveCard("yearly")}
                >
                    <div className="header">
                        <SVGComponent />
                        <h4>Yearly Income</h4>
                    </div>
                    <p className="income">
                        {USDollar.format(yearlyFinancials?.data?.[0]?.total || 0)}
                    </p>
                </Card>
            </IncomeSummary>

            <TableHeader>
                <p>Transactions</p>
                <div className="datepicker">
                    <DatePicker
                        date={selectedDate}
                        setDate={setSelectedDate}
                        minDate={minDate}
                        {...datePickerConfig[activeCard]}
                    />
                </div>
            </TableHeader>

            <TableContainer>
                {dataObj[activeCard].length ? (
                    <Table>
                        <thead>
                            <tr>
                                <Th></Th>
                                <Th>Person Name</Th>
                                <Th>Class Date</Th>
                                <Th>Type</Th>
                                <Th>Participants</Th>
                                <Th>Income</Th>
                                <Th>Class</Th>
                                <Th>Payment status</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {dataObj[activeCard]?.map((tx, index) => (
                                <tr key={index}>
                                    <Td>
                                        <Avatar>
                                            {tx?.student_profile_pic ? (
                                                <img src={getAvatarUrl(tx?.student_profile_pic)} />
                                            ) : (
                                                <UserIcon color="txt" />
                                            )}
                                        </Avatar>
                                    </Td>
                                    <Td>{tx.student_name}</Td>
                                    <Td style={{ whiteSpace: "nowrap" }}>
                                        {moment(new Date(tx.date)).format("MMM DD, YYYY")}
                                    </Td>
                                    <Td>{tx.class_type}</Td>
                                    <Td style={{ textAlign: "center" }}>
                                        {tx.participant_count}
                                    </Td>
                                    <Td>{USDollar.format(tx.booking_total_amount || 0)}</Td>
                                    <Td>
                                        <StatusIndicator status={tx.booking_status}>
                                            {tx.booking_status === "completed"
                                                ? "completed"
                                                : "cancelled"}
                                        </StatusIndicator>
                                    </Td>
                                    <Td>
                                        <PayoutIndicator payout={tx.payment_status}>
                                            {tx.payment_status === "Processed" ? "Processed" : "Hold"}
                                        </PayoutIndicator>
                                    </Td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                ) : (
                    <NoContent
                        title="You have no transactions"
                        image={NoFinancialsFound}
                        size={180}
                    />
                )}
            </TableContainer>
        </Container>
    );
};

const Container = styled.div`
  font-family: var(--font-secondary);
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex-grow: 1; 
  overflow: auto;

  @media (max-width: 1024px) {
    padding: 0px;
    
  }
`;

const IncomeSummary = styled.div`
  display: grid;
  gap: 1.5rem;
  grid-template-columns: repeat(3, 1fr);
  margin-bottom: 20px;

  @media (max-width: 1024px) {
    overflow-x: auto;
  }
`;

const Card = styled.div`
  cursor: pointer;
  background: ${({ active }) =>
        active ? defaultThemeColors.blue : defaultThemeColors.lightBlue};
  padding: 20px;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  color: ${({ active }) =>
        active ? defaultThemeColors.white : defaultThemeColors.blue};
  font-family: Poppins;

  & > .header {
    font-size: ${fonts.sizes.regular};
    font-weight: 600;
    line-height: 25.5px;
    text-align: left;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  & > .income {
    font-size: ${fonts.sizes["h3.4"]};
    font-weight: 700;
    line-height: 63px;
    text-align: right;
  }

  @media (max-width: 1024px) {
    width: 15rem;
  }
`;

const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  font-size: ${fonts.sizes.medium};
  font-weight: 600;
  line-height: 24.38px;

  & > .datepicker {
    min-width: 250px;

    & > div {
      width: 100%;
    }
  }
`;

const TableContainer = styled.div`
  width: 100%;
  background-color: ${defaultThemeColors.white};
  border: 2px solid ${defaultThemeColors.lightBlue};
  padding: 1rem;
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow: auto;
  min-height: 280px;

  & > P {
    text-align: center;
    font-weight: 500;
    padding: 1rem;
  }

  ${scrollbarStyles}
`;

const Table = styled.table`
  width: 100%;
  table-layout: auto;
  border-collapse: collapse;
  position: relative;

  & thead th {
    border-bottom: 2px solid ${defaultThemeColors.lightBlue};
    text-transform: uppercase;
    white-space: nowrap;
  }

  & tbody tr {
    border-bottom: 2px solid ${defaultThemeColors.lightBlue};
  }

  & tbody tr:last-child {
    border-bottom: none;
  }
`;

const Th = styled.th`
  padding: 1rem;
  text-align: left;
  color: ${defaultThemeColors.placeholder};
  font-size: ${fonts.sizes.small};
  font-weight: 600;
  position: sticky;
`;

const Td = styled.td`
  padding: 1rem;
  text-align: left;
  color: ${defaultThemeColors.txt};
  font-size: ${fonts.sizes.small};
  font-weight: 600;
`;

const Avatar = styled.div`
  height: 40px;
  width: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${defaultThemeColors.lightBlue};
  border-radius: 99px;
  cursor: pointer;

  & > svg {
    height: 24px;
    width: 24px;
  }

  & > img {
    height: 100%;
    width: 100%;
    object-fit: cover;
  }
`;

const StatusIndicator = styled.span`
  color: ${({ status }) =>
        status === "completed"
            ? "oklch(0.596 0.145 163.225)"
            : "oklch(0.577 0.245 27.325)"};
  font-weight: bold;
`;

const PayoutIndicator = styled.span`
  color: ${({ payout }) =>
        payout === "Processed"
            ? "oklch(0.596 0.145 163.225)"
            : "oklch(0.681 0.162 75.834)"};
  font-weight: bold;
`;

const NoFinancials = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 20px;
  height: 100%;
  width: 100%;

  .image-container {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 180px;
    height: 180px;
    border-radius: 999px;
    overflow: hidden;

    & > img {
      width: 100%;
      height: 100%;
      aspect-ratio: 1/1;
    }
  }

  .title {
    font-weight: 400;
    font-size: ${fonts.sizes.medium};
    line-height: 130%;
    text-align: center;
    color: ${defaultThemeColors.lightGrey};
  }
`;

const SVGComponent = (props) => (
    <svg
        width={20}
        height={20}
        viewBox="0 0 554.883 554.883"
        fill="currentColor"
        {...props}
    >
        <path d="M277.441 0C124.214 0 0 124.214 0 277.441s124.214 277.442 277.441 277.442 277.442-124.215 277.442-277.442S430.668 0 277.441 0m0 506.941c-126.546 0-229.5-102.955-229.5-229.5 0-126.546 102.954-229.5 229.5-229.5 126.545 0 229.5 102.954 229.5 229.5 0 126.545-102.955 229.5-229.5 229.5" />
        <path d="M277.441 63.241c-118.3 0-214.2 95.9-214.2 214.2s95.9 214.2 214.2 214.2 214.2-95.9 214.2-214.2-95.901-214.2-214.2-214.2m-15.3 31.619h30.599v53.042h-30.599zM147.902 292.74H94.86v-30.599h53.039v30.599zM292.74 460.021h-30.599V406.98h30.599zm3.358-185.894-12.873 101.008-27.357-122.002-.04-.021.031-.021-.031-.138.245-.012 108.07-75.809zM406.98 292.74v-30.599h53.039v30.599z" />
    </svg>
);
